import { useEffect, useRef, useState } from "react";
import useParentMessage from "../../utils/hooks/useParentMessage";

const BANNER_MARGIN = 18;

export interface BannerPositionStyle {
  position: "fixed" | "absolute";
  bottom?: number;
  top?: number;
  right: number;
}

/**
 * Returns CSS position styles for the cookie banner, or `null` in iframe mode
 * before the first scroll info arrives (the caller should suppress rendering
 * until a non-null value is returned).
 *
 * Standalone: `position: fixed; bottom: 18px; right: 18px` — always correct.
 *
 * Iframe: the app auto-resizes to full content height so the iframe's own
 * viewport equals the content height (e.g. 5000 px). `position: fixed` inside
 * such an iframe is relative to that full height, NOT to the visible screen.
 * This hook listens to `parentScrollInfo::` messages (same protocol as
 * useEAModalIframeScrolling) and computes an absolute `top` value that places
 * the banner at the visible bottom-right corner of the parent viewport.
 * Until the first message arrives the hook returns `null` so the banner stays
 * hidden rather than appearing at the wrong position.
 */
export function useCookieBannerIframePosition(
  bannerRef: React.RefObject<HTMLDivElement>,
): BannerPositionStyle | null {
  const isInIframe = window.self !== window.top;
  const { updateScrollPosition } = useParentMessage();
  const [bannerTop, setBannerTop] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isInIframe) return;

    // Kick off an initial request immediately and repeat every 600 ms so the
    // banner tracks the user scrolling in the parent page.
    updateScrollPosition();
    intervalRef.current = setInterval(() => {
      updateScrollPosition();
    }, 600);

    const handleMessage = (event: MessageEvent) => {
      const raw = typeof event.data === "string" ? event.data : "";
      if (!raw.startsWith("parentScrollInfo::")) return;

      const parts = raw.split("::");
      if (parts.length < 5) return;

      const parentScroll = parseFloat(parts[1]);
      const viewportHeight = parseFloat(parts[2]);
      const iframeTopPosition = parseFloat(parts[3]);

      if (isNaN(parentScroll) || isNaN(viewportHeight) || isNaN(iframeTopPosition)) return;

      // Bottom of the visible parent viewport translated into iframe coordinates
      const visibleBottom = parentScroll + viewportHeight - iframeTopPosition;
      const bannerHeight = bannerRef.current?.offsetHeight ?? 180;
      const computedTop = visibleBottom - bannerHeight - BANNER_MARGIN;

      setBannerTop(Math.max(computedTop, BANNER_MARGIN));
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInIframe]);

  // Standalone — always use a screen-fixed position.
  if (!isInIframe) {
    return { position: "fixed", bottom: BANNER_MARGIN, right: BANNER_MARGIN };
  }

  // Iframe — suppress rendering until we have real scroll data from the parent.
  // Returning null prevents the banner from flashing at the wrong position.
  if (bannerTop === null) return null;

  return { position: "absolute", top: bannerTop, right: BANNER_MARGIN };
}
